import { Vector as VectorLayer } from "ol/layer.js";
import { Draw, Modify, Snap, Select } from "ol/interaction.js";
import { extend } from "ol/extent.js";

import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import { Vector as VectorSource, TileWMS } from "ol/source.js";
import { getBottomLeft, getTopRight } from "ol/extent.js";
import MousePosition from "ol/control/MousePosition.js";
import { toStringXY } from "ol/coordinate.js";
import { defaults as defaultControls, FullScreen } from "ol/control.js";
import WKT from "ol/format/WKT.js";
import GeoJSON from "ol/format/GeoJSON.js";
import { transform, toLonLat } from "ol/proj.js";
import { Polygon, LineString } from "ol/geom.js";
import { bbox } from "ol/loadingstrategy.js";
import OSM from "ol/source/OSM.js";

import store from "@/store/store";

function addTileLayer(map, options) {
  const tileLayer = new TileLayer({
    name: options.name,
    source: new TileWMS({
      url: options.url,
      params: { LAYERS: options.layers, TILED: true },
      serverType: "mapserver"
    }),
    minZoom: 8,
    maxZoom: 18,
    zIndex: options.zIndex || 10
  });

  map.addLayer(tileLayer);

  return tileLayer;
}

function addWFSLayer(map) {
  const wfsSource = new VectorSource({
    name: "wfs",
    format: new GeoJSON(),
    url: function(extent) {
      return (
        "/mapserv/cgi-bin/mapserv?map=/map/webgis.map&service=WFS&" +
        "version=1.1.0&request=GetFeature&typename=dkn&" +
        "outputFormat=geojson&srsname=EPSG:3857&" +
        "bbox=" +
        extent.join(",") +
        ",EPSG:3857"
      );
    },
    strategy: bbox
  });

  const wfsLayer = new VectorLayer({
    source: wfsSource,
    minZoom: 19,
    maxZoom: 28,
    visible: false
  });

  map.addLayer(wfsLayer);

  return wfsLayer;
}

function addMoveEndListener(map) {
  function zoomChanged() {
    const zoom = map.getView().getZoom();

    console.log(`Zoom changed to ${zoom}`);
  }

  function wrapLon(value) {
    const worlds = Math.floor((value + 180) / 360);
    return value - worlds * 360;
  }

  function onMoveEnd(evt) {
    const map = evt.map;
    zoomChanged.call(this);
    const extent = map.getView().calculateExtent(map.getSize());
    const bottomLeft = toLonLat(getBottomLeft(extent));
    const topRight = toLonLat(getTopRight(extent));
    console.log("extent", [
      wrapLon(bottomLeft[0]),
      bottomLeft[1],
      wrapLon(topRight[0]),
      topRight[1]
    ]);
  }

  map.on("moveend", evt => onMoveEnd.call(this, evt));
}

function addPointerMoveHandler(map) {
  const pointerMoveHandler = function(evt) {
    if (evt.dragging) {
      return;
    }

    let helpMsg = "Click to start drawing";

    if (this.measure.sketch) {
      const geom = this.measure.sketch.getGeometry();
      if (geom instanceof Polygon) {
        helpMsg = this.measure.continuePolygonMsg;
      } else if (geom instanceof LineString) {
        helpMsg = this.measure.continueLineMsg;
      }

      this.measure.helpTooltipElement.innerHTML = helpMsg;
      this.measure.helpTooltip.setPosition(evt.coordinate);

      this.measure.helpTooltipElement.classList.remove("hidden");
    }
  };

  map.on("pointermove", evt => pointerMoveHandler.call(this, evt));

  const self = this;

  map.getViewport().addEventListener("mouseout", function() {
    if (self.measure.sketch) {
      self.measure.helpTooltipElement.classList.add("hidden");
    }
  });
}

function addMousePositionControl(map, options) {
  const mousePositionControl = new MousePosition({
    projection: `EPSG:${options.projection}`,
    className: "custom-mouse-position",
    target: document.getElementById("mouse-position"),
    undefinedHTML: "&nbsp;",
    coordinateFormat: coord => {
      const coord2 = [coord[1], coord[0]];
      const precision = options.projection == 4326 ? 6 : 2;
      return toStringXY(coord2, precision);
    }
  });

  mousePositionControl.setMap(map);
}

export default {
  /**
   * Init the map
   * @param {Object} settings
   */
  initMap(settings) {
    const { layers, view, hash } = settings;

    /*map.addLayer(new TileLayer({
        source: new OSM({
            url: 'http://mt{0-3}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
            attributions: [
                new Attribution({ html: '© Google' }),
                new Attribution({ html: '<a href="https://developers.google.com/maps/terms">Terms of Use.</a>' })
            ]
        })
    }));*/

    const map = new Map({
      controls: defaultControls({
        attribution: false
      }).extend([new FullScreen()]),
      target: "map",
      layers: [
        new TileLayer({
          source: new OSM(),
          zIndex: 1
        })
      ],
      view: new View({
        center: transform(view.center, `EPSG:${view.projection}`, "EPSG:3857"),
        zoom: view.zoom,
        minZoom: view.minZoom || 8,
        maxZoom: view.maxZoom || 26
      })
    });

    const op = {
      layers: layers.join(","),
      url: `/mapserv/cgi-bin/mapserv?map=/var/www/storage/app/map/tmp/${hash}.map&`, // TODO: remove full path
      name: "webgis",
      zIndex: 100
    };
    addTileLayer(map, op /*{ layers, mapfile }*/);
    // addWFSLayer(map);

    addMoveEndListener.call(this, map);
    addMousePositionControl(map, { projection: 3794 });
    addPointerMoveHandler.call(this, map);

    if (settings.extra_layers) {
      settings.extra_layers.forEach(layer => {
        if (layer.type == 6) {
          const options = {
            name: layer.name,
            zIndex: 10,
            ...layer.mapfile
          };
          addTileLayer(map, options);
        }
      });
    }

    return map;
  },

  /**
   * Clean up the added layers, interactions, etc.
   * TODO: Find better way
   *
   * @param {ol.Map} map The map instance
   */
  cleanup(map) {
    map.getLayers().forEach((layer, index) => {
      if (index >= 1) {
        map.removeLayer(layer);
      }
    });

    map.getInteractions().forEach((interaction, index) => {
      if (index >= 9) {
        map.removeInteraction(interaction);
      }
    });
  },

  /**
   * Fit the view for layer / features
   *
   * @param {ol.Map} map The map instance
   * @param {object} options The options
   * @param {ol.layer.Vector} options.layer The options
   */
  fitView(map, options) {
    const layer = options.layer;

    const extent = layer
      .getSource()
      .getFeatures()[0]
      .getGeometry()
      .getExtent()
      .slice(0);

    layer
      .getSource()
      .getFeatures()
      .forEach(function(feature) {
        extend(extent, feature.getGeometry().getExtent());
      });

    map.getView().fit(extent, map.getSize());

    if (options.maxZoom && map.getView().getZoom() > options.maxZoom) {
      map.getView().setZoom(options.maxZoom);
    }
  },

  /**
   * @param {ol.Map} map The map object reference
   * @param {object} options The extra options related to drawing
   * @param {string} options.type The object type
   * @param {string} options.maxPoints The max points
   * @param {string} [options.layer] The preferred layer to draw on
   * @param {object} options.snap The extra snap functionality
   * @param {ol.Layer} options.snap.layer The layer used for snap functionality
   * @param {ol.Feature[]} options.snap.features The features used for snap functionality
   * @param callback
   */
  setDrawing(map, options, callback) {
    let drawLayer = options.layer;

    if (!drawLayer) {
      drawLayer = new VectorLayer({
        title: "drawLayer",
        source: new VectorSource()
      });

      map.addLayer(drawLayer);
      options.drawLayer = drawLayer;
    }

    const draw = new Draw({
      source: drawLayer.getSource(),
      type: options.type,
      maxPoints: options.maxPoints
    });

    draw.on("drawend", function(evt) {
      const wkt = new WKT();
      const geometry = evt.feature.getGeometry();
      // const transformed = geometry.transform("EPSG:3857", "EPSG:3787");

      const geom = wkt.writeGeometry(geometry /*transformed*/);

      callback(geom);
    });

    map.addInteraction(draw);

    if (options.snap && (options.snap.layer || options.snap.features)) {
      const usedOn = options.snap.layer
        ? { source: options.snap.layer.getSource() }
        : { features: options.snap.features };

      const snapOptions = {
        pixelTolerance: store.state.config.pixelTolerance
      };

      Object.assign(snapOptions, usedOn);
      const snap = new Snap(snapOptions);
      map.addInteraction(snap);
    }
  },

  /**
   * @param {ol.Map} map The map object reference
   * @param {object} options The extra options related to drawing
   * @param {ol.Layer} options.layer The layer to be modified type
   * @param {ol.Feature[]} options.features The features to be modified
   * @param {object} options.snap The extra snap functionality
   * @param {ol.Layer} options.snap.layer The layer used for snap functionality
   * @param {ol.Feature[]} options.snap.features The features used for snap functionality
   * @param {object} callback The modify end callback
   */
  setModifying(map, options, callback = null) {
    const modifyOptions = options.layer
      ? { source: options.layer.getSource() }
      : { features: options.features };

    const modify = new Modify(modifyOptions);

    map.addInteraction(modify);

    if (callback) {
      this.addFeatureEvents(modify, callback);
    }

    if (options.snap && (options.snap.layer || options.snap.features)) {
      const usedOn = options.snap.layer
        ? { source: options.snap.layer.getSource() }
        : { features: options.snap.features };

      const snapOptions = {
        pixelTolerance: store.state.config.pixelTolerance
      };

      Object.assign(snapOptions, usedOn);
      const snap = new Snap(snapOptions);
      map.addInteraction(snap);
    }
  },

  addFeatureEvents(modify, callback) {
    const originalCoordinates = {};
    modify.on("modifystart", function(evt) {
      evt.features.forEach(function(feature) {
        originalCoordinates[feature] = feature.getGeometry().getCoordinates();
      });
    });
    modify.on("modifyend", function(evt) {
      evt.features.forEach(function(feature) {
        if (feature in originalCoordinates && Math.random() > 0.5) {
          const coordinates = feature.getGeometry().getCoordinates();
          if (
            coordinates[0] != originalCoordinates[feature][0] ||
            coordinates[1] != originalCoordinates[feature][1]
          ) {
            callback(feature);
          }
        }
      });
    });
  },

  /**
   * @param {ol.Map} map
   * @param {object} options
   * @param {ol.Layer} options.layer
   * @param {ol.events.condition|null} options.condition
   * @param {object} callback
   */
  setSelect(map, options, callback) {
    const selectDoubleClick = new Select({
      multi: false,
      condition: options.condition ? options.condition : null
    });

    selectDoubleClick.on("select", function(evt) {
      if (evt.selected.length) {
        const feature = evt.selected[0];

        if (options.layer) {
          if (options.layer !== selectDoubleClick.getLayer(feature)) {
            console.log("incorrect layer", selectDoubleClick.getLayer(feature));
            return;
          }
        }

        callback(feature);
      }
    });

    map.addInteraction(selectDoubleClick);
  }
};
